import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Slider, { HANDLE_TYPE } from './Slider';
import { View } from 'foundations-components/components/View';
import { SLIDER_HEIGHT_PX, TrackContainer } from './TrackContainer';
import { MEDIA_PLAYER_COLORS } from 'recording-review-components-lib/media-player/constants/Colors';
import { MediaTimelineHighlight } from '../components/MediaTimelineComponents/MediaTimelineHighlight';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDualSliderContainer = styled.div.withConfig({
  displayName: "DualSliders__StyledDualSliderContainer"
})(["pointer-events:auto;"]);
export const DualSliders = ({
  startValue = 0,
  endValue = 100,
  trackStart = 1,
  trackEnd = 100,
  step = 1,
  onStartHandleDrag,
  onEndHandleDrag,
  onStartHandleMouseUp,
  onEndHandleMouseUp,
  onStartTrackClick,
  onEndTrackClick
}) => {
  const totalTrackLength = trackEnd - trackStart;
  const trackRef = useRef(null);
  const [startSliderValue, setStartSliderValue] = useState(Math.max(startValue, trackStart));
  const [endSliderValue, setEndSliderValue] = useState(Math.min(endValue, trackEnd));
  useEffect(() => {
    setStartSliderValue(Math.max(startValue, trackStart));
  }, [startValue, trackStart]);
  useEffect(() => {
    setEndSliderValue(Math.min(endValue, trackEnd));
  }, [endValue, trackEnd]);
  const calculateValue = useCallback((positionX, minValue, maxValue) => {
    if (trackRef && trackRef.current) {
      const trackLength = totalTrackLength / step;
      const trackRect = trackRef.current.getBoundingClientRect();
      let newValue = (positionX - trackRect.x) / trackRect.width * trackLength;
      newValue = Math.min(newValue, maxValue);
      newValue = Math.max(newValue, minValue);
      return Math.round(newValue);
    }
    return 0;
  }, [totalTrackLength, step]);
  const handleOnStartHandleDrag = useCallback(positionX => {
    const newValue = calculateValue(positionX, 0, endSliderValue - 1);
    setStartSliderValue(newValue);
    if (onStartHandleDrag) {
      onStartHandleDrag(newValue);
    }
  }, [endSliderValue, onStartHandleDrag, calculateValue]);
  const handleOnStartHandleMouseUp = useCallback(positionX => {
    if (onStartHandleMouseUp) {
      const newValue = calculateValue(positionX, 0, endSliderValue - 1);
      onStartHandleMouseUp(newValue);
    }
  }, [endSliderValue, onStartHandleMouseUp, calculateValue]);
  const handleOnStartTrackClick = useCallback(evt => {
    if (onStartTrackClick) {
      const newValue = calculateValue(evt.clientX, 0, endSliderValue - 1);
      onStartTrackClick(newValue);
    }
  }, [endSliderValue, onStartTrackClick, calculateValue]);
  const handleOnEndHandleDrag = useCallback(positionX => {
    const newValue = calculateValue(positionX, startSliderValue + 1, trackEnd);
    setEndSliderValue(newValue);
    if (onEndHandleDrag) {
      onEndHandleDrag(newValue);
    }
  }, [startSliderValue, trackEnd, onEndHandleDrag, calculateValue]);
  const handleOnEndHandleMouseUp = useCallback(positionX => {
    if (onEndHandleMouseUp) {
      const newValue = calculateValue(positionX, startSliderValue + 1, trackEnd);
      onEndHandleMouseUp(newValue);
    }
  }, [startSliderValue, trackEnd, onEndHandleMouseUp, calculateValue]);
  const handleOnEndTrackClick = useCallback(evt => {
    if (onEndTrackClick) {
      const newValue = calculateValue(evt.clientX, startSliderValue + 1, trackEnd);
      onEndTrackClick(newValue);
    }
  }, [startSliderValue, trackEnd, onEndTrackClick, calculateValue]);
  const clipStartPositionPercent = useMemo(() => startSliderValue / totalTrackLength * 100, [startSliderValue, totalTrackLength]);
  const clipEndPositionPercent = useMemo(() => endSliderValue ? totalTrackLength - endSliderValue < 1 ? 100 : endSliderValue / totalTrackLength * 100 : 100, [endSliderValue, totalTrackLength]);
  return /*#__PURE__*/_jsx(StyledDualSliderContainer, {
    children: /*#__PURE__*/_jsx(View, {
      inlineSize: "100%",
      marginBlock: "-100",
      children: /*#__PURE__*/_jsxs(View, {
        position: "relative",
        blockSize: `${SLIDER_HEIGHT_PX}px`,
        inlineSize: "100%",
        ref: trackRef,
        children: [/*#__PURE__*/_jsx(MediaTimelineHighlight, {
          startPosition: Math.max(clipStartPositionPercent, 0),
          endPosition: Math.min(clipEndPositionPercent, 100),
          "data-test-id": "clip-highlight",
          "data-acceptance-test": "clip-highlight",
          "data-from": startSliderValue,
          "data-to": endSliderValue
        }), /*#__PURE__*/_jsx(TrackContainer, {
          left: 0,
          width: (startSliderValue - trackStart) / totalTrackLength,
          cursor: "pointer",
          onClick: handleOnStartTrackClick,
          testId: "from-track",
          children: /*#__PURE__*/_jsx(View, {
            blockSize: "4px",
            marginBlock: "300",
            borderStartStartRadius: "100",
            borderEndStartRadius: "100",
            backgroundColor: MEDIA_PLAYER_COLORS.DEFAULT_STATUS_FILL
          })
        }), /*#__PURE__*/_jsx(TrackContainer, {
          left: (endSliderValue - trackStart) / totalTrackLength,
          width: (trackEnd - endSliderValue) / totalTrackLength,
          cursor: "pointer",
          onClick: handleOnEndTrackClick,
          testId: "to-track",
          children: /*#__PURE__*/_jsx(View, {
            blockSize: "4px",
            marginBlock: "300",
            borderStartEndRadius: "100",
            borderEndEndRadius: "100",
            backgroundColor: MEDIA_PLAYER_COLORS.DEFAULT_STATUS_FILL
          })
        }), /*#__PURE__*/_jsx(Slider, {
          value: startSliderValue,
          trackStart: trackStart,
          trackEnd: trackEnd,
          minValue: trackStart,
          maxValue: endSliderValue - 1,
          onDrag: handleOnStartHandleDrag,
          onMouseUp: handleOnStartHandleMouseUp,
          handleType: HANDLE_TYPE.FROM,
          dataUnitTest: "from-slider",
          dataAcceptanceTest: "from-slider"
        }), /*#__PURE__*/_jsx(Slider, {
          value: endSliderValue,
          trackStart: trackStart,
          trackEnd: trackEnd,
          minValue: startSliderValue + 1,
          maxValue: trackEnd,
          onDrag: handleOnEndHandleDrag,
          onMouseUp: handleOnEndHandleMouseUp,
          handleType: HANDLE_TYPE.TO,
          dataUnitTest: "to-slider",
          dataAcceptanceTest: "to-slider"
        })]
      })
    })
  });
};